import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LiveDataProvider } from "@/context/LiveDataContext";
import { resolvePilotDrone, getLastPilotDrone } from "@/lib/pilotageNavigation";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import LiveMap from "@/pages/LiveMap";
import Drones from "@/pages/Drones";
import Missions from "@/pages/Missions";
import FlightSettings from "@/pages/FlightSettings";
import Logs from "@/pages/Logs";
import Drop from "@/pages/Drop";
import ManualControl from "@/pages/ManualControl";
import Infrastructure from "@/pages/Infrastructure";
import AIAssistant from "@/pages/AIAssistant";
import DroneRegistry from "@/pages/DroneRegistry";
import AdminTests from "@/pages/AdminTests";
import MapNavLab from "@/pages/MapNavLab";
import EsriQualification from "@/pages/EsriQualification";
import FlightSessions from "@/pages/FlightSessions";
import FlightSessionDetail from "@/pages/FlightSessionDetail";
import { useLiveData } from "@/context/LiveDataContext";

// WEBXR.2 — cockpit immersif Quest 3 (chargement lazy pour isoler le bundle 3D du cockpit DOM).
const CockpitXR = lazy(() => import("@/webxr/CockpitXR"));

function Loader() {
  return <div className="h-screen flex items-center justify-center bg-[#0A0A0A] font-mono text-xs text-[#525252] tracking-widest uppercase">Chargement...</div>;
}

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function ManualControlEntry() {
  const { drones } = useLiveData();
  if (!drones.length) return <Loader />;
  // UNIFIED.4 : dernier drone mémorisé -> premier ONLINE -> premier disponible (jamais drones[0] en dur).
  const target = resolvePilotDrone(drones, getLastPilotDrone());
  if (!target) return <Loader />;
  return <Navigate to={`/manual-control/${target}`} replace />;
}

// UNIFIED.7 : /real-control/:id est un legacy bookmark. Redirect pur vers le cockpit physique UNIQUE
// (/manual-control/:id), sans prise de lease ni montage d'un second cockpit. L'ID drone est préservé.
function LegacyRealControlRedirect() {
  const { drone_id } = useParams();
  return <Navigate to={`/manual-control/${encodeURIComponent(drone_id)}`} replace />;
}

function Shell() {
  return (
    <LiveDataProvider>
      <Layout />
    </LiveDataProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster theme="dark" position="top-right" toastOptions={{ style: { fontFamily: "JetBrains Mono, monospace", fontSize: "12px" } }} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/vr/:drone_id" element={<Protected admin><LiveDataProvider><Suspense fallback={<Loader />}><CockpitXR /></Suspense></LiveDataProvider></Protected>} />
          <Route element={<Protected><Shell /></Protected>}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/map" element={<LiveMap />} />
            <Route path="/drones" element={<Drones />} />
            <Route path="/missions" element={<Missions />} />
            <Route path="/flight-sessions" element={<FlightSessions />} />
            <Route path="/flight-sessions/:drone_id/:session_id" element={<FlightSessionDetail />} />
            <Route path="/settings" element={<FlightSettings />} />
            <Route path="/logs" element={<Logs />} />
            <Route path="/drop" element={<Drop />} />
            <Route path="/manual-control" element={<ManualControlEntry />} />
            <Route path="/manual-control/:drone_id" element={<ManualControl />} />
            <Route path="/real-control/:drone_id" element={<LegacyRealControlRedirect />} />
            <Route path="/assistant" element={<AIAssistant />} />
            <Route path="/drone-registry" element={<Protected admin><DroneRegistry /></Protected>} />
            <Route path="/infrastructure" element={<Protected admin><Infrastructure /></Protected>} />
            <Route path="/tests" element={<Protected admin><AdminTests /></Protected>} />
            <Route path="/map-lab" element={<Protected admin><MapNavLab /></Protected>} />
            <Route path="/esri-qualification" element={<Protected admin><EsriQualification /></Protected>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
